import { ComponentPublicInstance, type App } from 'vue'
import { QueuedEvent } from '@/js/services/log/sentry-types'
import type { IRogueLog } from '@types'
import type { IRogue } from '../RogueTypes'

const hideConsole = window.ROGUE.env === 'prod' && !window.ROGUE._dbg
const canLoadSentry = window.ROGUE.clientKeys.sentry && (window.ROGUE.env === 'prod' || window.ROGUE.env === 'staging')

let rogue: IRogue | undefined

let events: QueuedEvent[] = []
let vueEvents: { msg?: string, err?: Error, instance: ComponentPublicInstance | null, info: string, level: 'warn' | 'error' }[] = []

let sentryLoaded = false

let loadPromise: Promise<unknown> | undefined

let app: App | undefined

// Load the actual sentry library
function loadSentry(): Promise<unknown> | undefined {
  // Already loaded
  if (!loadPromise) {
    loadPromise = import(/* webpackChunkName: "sntry-lgger" */ './sentry').then(sentry => {
      const Sentry = sentry.init(app!, rogue!)
      sentryLoaded = true
      // Re-fire all the queued events
      events.forEach(event => {
        if (event.crumb) {
          Sentry.addBreadcrumb(event.crumb)
        } else if (event.error && event.message) {
          rogue!.log.error(event.message, event.error, event.data)
        } else if (event.message) {
          rogue!.log.warn(event.message, event.data)
        } else if (event.user) {
          Sentry.setUser(event.user)
        } else if (event.tags) {
          Sentry.setTags(event.tags)
        }
      })
      events = []

      vueEvents.forEach(vueEvent => {
        if (vueEvent.level === 'error') {
          app?.config.errorHandler?.(vueEvent.err!, vueEvent.instance, vueEvent.info)
        } else {
          app?.config.warnHandler?.(vueEvent.msg!, vueEvent.instance, vueEvent.info)
        }
      })
      vueEvents = []
    })
  }

  return loadPromise
}

function addEvent(event: QueuedEvent) {
  // If we dont have sentry, theres no point in queueing events
  if (canLoadSentry) {
    events.push(event)
  }
}

export const BrowserLog: IRogueLog = {
  // eslint-disable-next-line no-console
  info  : hideConsole ? () => { } : console.log,
  // eslint-disable-next-line no-console
  debug : hideConsole ? () => { } : console.debug,
  crumb : (category: string, message: string, data: { [key: string]: unknown }) => {
    if (!hideConsole) {
      // eslint-disable-next-line no-console
      console.debug(`Crumb: ${category}: ${message}`, data)
    }
    addEvent({ crumb: { category, message, data } })
  },
  set: (type: 'user' | 'tag', data: Record<string, any>) => {
    if (type === 'user') {
      addEvent({ user: data })
    } else {
      addEvent({ tags: data })
    }
  },
  warn: (message: string, extra: Record<string, any>) => {
    if (!hideConsole) {
      // eslint-disable-next-line no-console
      console.warn(message, extra)
    }

    addEvent({ message, data: extra })
    if (canLoadSentry) {
      BrowserLog.info('First warning encountered, loading sentry...')
      loadSentry()
    }
  },
  error: (message: string, error: string | Error, extra: Record<string, any> = {}) => {
    if (!hideConsole) {
      // eslint-disable-next-line no-console
      console.error(message, error, extra)
    }

    addEvent({ error, message, data: extra })
    if (canLoadSentry) {
      BrowserLog.info('First error encountered, loading sentry...')
      loadSentry()
    }
  },
}

if (!canLoadSentry) {
  BrowserLog.info('Sentry not loaded', window.ROGUE.release)
}

export function setBrowserRogue(rg: IRogue) {
  rogue = rg
}

export function setBrowserVueApp(vueApp: App) {
  app = vueApp

  if (canLoadSentry) {
    app.config.errorHandler = (err: unknown, instance: ComponentPublicInstance | null, info: string) => {
      if (!sentryLoaded) {
        vueEvents.push({ err: err as Error, instance, info, level: 'error' })
        loadSentry()
      }
    }
    app.config.warnHandler = (msg: string, instance: ComponentPublicInstance | null, trace: string) => {
      if (!sentryLoaded) {
        vueEvents.push({ level: 'warn', instance, info: trace, msg })
      }
    }
  }
}

window.onerror = function catchUncaught(message, source, lineno, colno, error) {
  rogue?.log.error(message, error, { source, lineno, colno })
}

document.addEventListener('visibilitychange', () => {
  const isHidden = document.visibilityState === 'hidden'
  rogue?.log.crumb('browser', isHidden ? 'Browser tab hidden' : 'Browser tab visible')
}, false)
